import { Package } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { NurseryInventoryRow } from '../../types/nursery';
import PbrMark from '../cultivar/PbrMark';
import PbrNotice from '../cultivar/PbrNotice';
import { isPbrProtected } from '../../constants/pbr';

interface InventoryGridProps {
  inventory: NurseryInventoryRow[];
}

function InventoryGrid({ inventory = [] }: InventoryGridProps) {
  const { t } = useTranslation('nursery', { keyPrefix: 'InventoryGrid' });
  if (!inventory.length) {
    return (
      <div className="text-center py-8 text-neutral-400">
        <Package className="h-10 w-10 mx-auto mb-2 opacity-40" />
        <p className="text-sm">{t('empty')}</p>
        <p className="text-xs mt-1">{t('emptyHelp')}</p>
      </div>
    );
  }

  return (
    <div className="border border-neutral-200 rounded-lg overflow-hidden">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-neutral-100 bg-neutral-50/50">
            <th className="text-left px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.cultivar')}</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.rootstock')}</th>
            <th className="text-left px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.type')}</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.growing')}</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.ready')}</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.onOrder')}</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.allocated')}</th>
            <th className="text-right px-4 py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">{t('columns.available')}</th>
          </tr>
        </thead>
        <tbody>
          {inventory.map((row) => {
            const available = row.est_avail_count;
            const isPositive = available > 0;

            return (
              <tr key={row.id} className="border-b border-neutral-50 last:border-b-0">
                <td className="px-4 py-3">
                  <span className="font-medium text-neutral-800">
                    {row.cultivar_name ?? '—'}
                    <PbrMark status={row.cultivar_protection_status} />
                  </span>
                </td>
                <td className="px-4 py-3 text-neutral-600">{row.rootstock_name ?? '—'}</td>
                <td className="px-4 py-3 text-neutral-600">
                  {row.tree_type_name ?? row.tree_type_code ?? '—'}
                  {row.stock_type && (
                    <span className={`ml-1.5 inline-flex px-1.5 py-0.5 rounded text-xs font-medium ${
                      row.stock_type === 'B'
                        ? 'bg-amber-100 text-amber-700'
                        : 'bg-teal-100 text-teal-700'
                    }`}>
                      {row.stock_type === 'B' ? 'B' : 'P'}
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-neutral-600">{row.amt_growing}</td>
                <td className="px-4 py-3 text-right text-neutral-600">{row.amt_ready}</td>
                <td className="px-4 py-3 text-right text-neutral-600">{row.amt_on_order}</td>
                <td className="px-4 py-3 text-right text-neutral-600">{row.amt_allocated}</td>
                <td className="px-4 py-3 text-right">
                  <span className={`font-semibold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {available}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {inventory.some((r) => isPbrProtected(r.cultivar_protection_status)) && (
        <div className="px-4 py-3 border-t border-neutral-100 bg-neutral-50/50">
          <PbrNotice bare />
        </div>
      )}
    </div>
  );
}

export default InventoryGrid;
